import React, { useState } from 'react';
import { InfoModal, InfoButton, infoContent } from './InfoModal';

const handleNonNegativeInput = (e) => {
  const { value, min, max } = e.target;
  const numValue = parseFloat(value);
  
  if (value === '') {
    return '';
  }
  
  if (numValue < 0) {
    return 0;
  }
  
  if (max && numValue > parseFloat(max)) {
    return parseFloat(max);
  }
  
  if (min && numValue < parseFloat(min)) {
    return parseFloat(min);
  }
  
  return numValue;
};

const IndoorUsage = ({ formData, updateFormData }) => {
  const [openSection, setOpenSection] = useState(null);
  
  // Add state for the info modal
  const [modalInfo, setModalInfo] = useState({
    isOpen: false,
    title: '',
    content: null,
    image: null
  });

  // Function to open the info modal
  const openInfoModal = (infoType) => {
    const info = infoContent[infoType];
    setModalInfo({
      isOpen: true,
      title: info.title,
      content: info.content,
      image: info.image
    });
  };

  // Function to close the info modal
  const closeInfoModal = () => {
    setModalInfo({
      ...modalInfo,
      isOpen: false
    });
  };

  const handleInputChange = (e) => {
    const { name, type } = e.target;
    let value;

    if (type === 'number') {
      value = handleNonNegativeInput(e);
    } else {
      value = e.target.value;
    }

    updateFormData('indoor', { [name]: value });
  };

  const toggleSection = (section) => {
    if (openSection === section) {
      setOpenSection(null);
    } else {
      setOpenSection(section);
    }
  };

  // Only count a section as filled if the user has actually interacted with it
  const isSectionFilled = (sectionId) => {
    switch(sectionId) {
      case 'bathroom':
        return formData?.bathingMethodUpdated === true;
      case 'toilet':
        return formData?.toiletTypeUpdated === true || formData?.toiletFlushesUpdated === true;
      case 'faucet':
        return formData?.faucetMinutesUpdated === true || formData?.efficientFaucetsUpdated === true;
      case 'dishes':
        return formData?.dishwashingMethodUpdated === true;
      case 'laundry':
        return formData?.laundryMethodUpdated === true;
      default:
        return false;
    }
  };

  // Check if specific sub-option has been interacted with
  const isOptionFilled = (optionKey) => {
    return formData?.[`${optionKey}Updated`] === true;
  };

  const updateMethodWithFlag = (field, value) => {
    updateFormData('indoor', { 
      [field]: value,
      [`${field}Updated`]: true 
    });
  };

  const SmallCheckIcon = () => (
    <svg 
      className="h-4 w-4 text-green-500 inline-block ml-1" 
      viewBox="0 0 20 20" 
      fill="currentColor"
    >
      <path 
        fillRule="evenodd" 
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
        clipRule="evenodd" 
      />
    </svg>
  );

  const CheckIcon = () => (
    <svg 
      className="h-5 w-5 text-green-500" 
      viewBox="0 0 20 20" 
      fill="currentColor"
    >
      <path 
        fillRule="evenodd" 
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
        clipRule="evenodd" 
      />
    </svg>
  );

  const renderSection = (section, content) => (
    <div className="border rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(section.id)}
        className="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-left flex items-center gap-2">
            {section.label}
          </h3>
          {isSectionFilled(section.id) && (
            <CheckIcon />
          )}
        </div>
        <svg
          className={`w-6 h-6 transform transition-transform ${
            openSection === section.id ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {openSection === section.id && (
        <div className="px-4 py-3 bg-gray-50 border-t">
          {content}
        </div>
      )}
    </div>
  );

  return (
    <div className="section-container max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-blue-700">Indoor Water Usage</h2>
      <p className="text-gray-600 mb-6">
        Please provide information about your daily water usage habits.
      </p>

      {/* Add Info Modal Component */}
      <InfoModal
        isOpen={modalInfo.isOpen}
        onClose={closeInfoModal}
        title={modalInfo.title}
        content={modalInfo.content}
        image={modalInfo.image}
      />

      <div className="space-y-4">
        {/* Bathroom Water Usage Section */}
        {renderSection(
          { id: 'bathroom', label: '🚿 Bathroom Water Usage' },
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                How do you take a bath?
              </label>
              <div className="grid grid-cols-1 gap-4 mt-2">
                {/* Shower Option */}
                <div className="space-y-4">
                  <div 
                    onClick={() => updateMethodWithFlag('bathingMethod', 'shower')}
                    className={`p-2 border-2 rounded-lg cursor-pointer flex justify-between items-center
                      ${formData?.bathingMethod === 'shower'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <span>Shower</span>
                    {(formData?.showerFrequencyUpdated || formData?.showerDurationUpdated || formData?.efficientShowerheadUpdated) && <SmallCheckIcon />}
                  </div>
                  
                  {formData?.bathingMethod === 'shower' && (
                    <div className="ml-4 space-y-4 border-l-2 pl-4 border-blue-200">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          How many showers do you take per week?
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="showerFrequency"
                            min="0"
                            max="14"
                            step="1"
                            value={formData?.showerFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { showerFrequencyUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="showerFrequency"
                            min="0"
                            max="21"
                            value={formData?.showerFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { showerFrequencyUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          How long do you spend in the shower (minutes)?
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="showerDuration"
                            min="1"
                            max="30"
                            step="1"
                            value={formData?.showerDuration || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { showerDurationUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="showerDuration"
                            min="1"
                            max="30"
                            value={formData?.showerDuration || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { showerDurationUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                          Do you have a water-efficient showerhead?
                          <InfoButton onClick={() => openInfoModal('showerhead')} />
                        </label>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div
                            onClick={() => updateMethodWithFlag('efficientShowerhead', true)}
                            className={`p-2 border-2 rounded-lg cursor-pointer text-center
                              ${formData?.efficientShowerhead === true
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'}`}
                          >
                            Yes
                          </div>
                          <div
                            onClick={() => updateMethodWithFlag('efficientShowerhead', false)}
                            className={`p-2 border-2 rounded-lg cursor-pointer text-center
                              ${formData?.efficientShowerhead === false
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'}`}
                          >
                            No/Not Sure
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1 text-right">
                          <span 
                            className="text-blue-600 hover:underline cursor-pointer"
                            onClick={() => openInfoModal('showerhead')}
                          >
                            How to identify an efficient showerhead?
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bucket Bath Option */}
                <div className="space-y-4">
                  <div
                    onClick={() => updateMethodWithFlag('bathingMethod', 'bucket')}
                    className={`p-2 border-2 rounded-lg cursor-pointer flex justify-between items-center
                      ${formData?.bathingMethod === 'bucket'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <span>Bucket Bath</span>
                    {formData?.bucketBathFrequencyUpdated && <SmallCheckIcon />}
                  </div>
                  
                  {formData?.bathingMethod === 'bucket' && (
                    <div className="ml-4 space-y-4 border-l-2 pl-4 border-blue-200">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          How many bucket baths do you take per week?
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="bucketBathFrequency"
                            min="1"
                            max="14"
                            step="1"
                            value={formData?.bucketBathFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { bucketBathFrequencyUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="bucketBathFrequency"
                            min="1"
                            max="14"
                            value={formData?.bucketBathFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { bucketBathFrequencyUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                        average bucket size 25 liters
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bathtub Option */}
                <div className="space-y-4">
                  <div
                    onClick={() => updateMethodWithFlag('bathingMethod', 'bathtub')}
                    className={`p-2 border-2 rounded-lg cursor-pointer flex justify-between items-center
                      ${formData?.bathingMethod === 'bathtub'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <span>Bathtub</span>
                    {formData?.bathtubFrequencyUpdated && <SmallCheckIcon />}
                  </div>
                  
                  {formData?.bathingMethod === 'bathtub' && (
                    <div className="ml-4 space-y-4 border-l-2 pl-4 border-blue-200">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          How many bathtub baths do you take per month?
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="bathtubFrequency"
                            min="1"
                            max="30"
                            step="1"
                            value={formData?.bathtubFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { bathtubFrequencyUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="bathtubFrequency"
                            min="1"
                            max="30"
                            value={formData?.bathtubFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { bathtubFrequencyUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Each bathtub bath uses approximately 300 liters of water
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toilet Section */}
        {renderSection(
          { id: 'toilet', label: '🚽 Toilet Usage' },
          <div className="space-y-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                Does your home use treated water for flushing?
              </label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  onClick={() => updateMethodWithFlag('usesTreatedWater', true)}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.usesTreatedWater === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  <span>Yes</span> <span className="text-xs text-gray-500">Recycled/Grey water</span>
                </div>
                <div
                  onClick={() => updateMethodWithFlag('usesTreatedWater', false)}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.usesTreatedWater === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  <span>No</span> <span className="text-xs text-gray-500">Regular water supply</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                If you use recycled/grey water for flushing, it won't count toward your water footprint
              </p>
            </div>

            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                How many times do you flush the toilet per day?
                {isOptionFilled('toiletFlushes')}
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  name="toiletFlushes"
                  min="1"
                  max="15"
                  step="1"
                  value={formData?.toiletFlushes || 0}
                  onChange={(e) => {
                    handleInputChange(e);
                    updateFormData('indoor', { toiletFlushesUpdated: true });
                  }}
                  className="w-full"
                />
                <input
                  type="number"
                  name="toiletFlushes"
                  min="1"
                  max="15"
                  value={formData?.toiletFlushes || 0}
                  onChange={(e) => {
                    handleInputChange(e);
                    updateFormData('indoor', { toiletFlushesUpdated: true });
                  }}
                  className="w-20 text-center border rounded p-1"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                Type of toilet flush:
                <InfoButton onClick={() => openInfoModal('toilet')} />
              </label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {[
                  { 
                    id: 'traditional', 
                    label: 'Traditional Flush'
                  },
                  { 
                    id: 'dualFlush', 
                    label: 'Dual Flush'
                  },
                  { 
                    id: 'efficientDual', 
                    label: 'Efficient Dual Flush'
                  }
                ].map(type => (
                  <div
                    key={type.id}
                    onClick={() => updateMethodWithFlag('toiletType', type.id)}
                    className={`p-2 border-2 rounded-lg cursor-pointer
                      ${formData?.toiletType === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <span>{type.label}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-1 text-right">
                <span 
                  className="text-blue-600 hover:underline cursor-pointer"
                  onClick={() => openInfoModal('toilet')}
                >
                  What's the difference between toilet types?
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Faucet Usage */}
        {renderSection(
          { id: 'faucet', label: '🚰 Faucet Usage' },
          <div className="space-y-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                Minutes of faucet running per day (brushing teeth, washing hands, etc.):
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  name="faucetMinutes"
                  min="1"
                  max="60"
                  step="1"
                  value={formData?.faucetMinutes || 0}
                  onChange={(e) => {
                    handleInputChange(e);
                    updateFormData('indoor', { faucetMinutesUpdated: true });
                  }}
                  className="w-full"
                />
                <input
                  type="number"
                  name="faucetMinutes"
                  min="1"
                  max="60"
                  value={formData?.faucetMinutes || 0}
                  onChange={(e) => {
                    handleInputChange(e);
                    updateFormData('indoor', { faucetMinutesUpdated: true });
                  }}
                  className="w-20 text-center border rounded p-1"
                />
              </div>
            </div>
            
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                Do you have water-efficient faucet aerators?
                <InfoButton onClick={() => openInfoModal('faucets')} />
              </label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  onClick={() => updateMethodWithFlag('efficientFaucets', true)}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.efficientFaucets === true
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  Yes
                </div>
                <div
                  onClick={() => updateMethodWithFlag('efficientFaucets', false)}
                  className={`p-2 border-2 rounded-lg cursor-pointer text-center
                    ${formData?.efficientFaucets === false
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  No/Not Sure 
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1 text-right">
                <span 
                  className="text-blue-600 hover:underline cursor-pointer"
                  onClick={() => openInfoModal('faucets')}
                >
                  How to identify efficient faucet aerators?
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Dishwashing */}
        {renderSection(
          { id: 'dishes', label: '🍽️ Dishwashing' },
          <div className="space-y-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                How do you wash your dishes?
              </label>
              <div className="grid grid-cols-1 gap-4 mt-2">
                {/* Dishwasher Option */}
                <div className="space-y-4">
                  <div
                    onClick={() => updateMethodWithFlag('dishwashingMethod', 'dishwasher')}
                    className={`p-2 border-2 rounded-lg cursor-pointer
                      ${formData?.dishwashingMethod === 'dishwasher'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    Dishwasher
                    {isOptionFilled('dishwasherFrequency') && <SmallCheckIcon />}
                  </div>
                  
                  {formData?.dishwashingMethod === 'dishwasher' && (
                    <div className="ml-4 space-y-4 border-l-2 pl-4 border-blue-200">
                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                          How many times do you run the dishwasher per day?
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="dishwasherFrequency"
                            min="1"
                            max="5"
                            step="1"
                            value={formData?.dishwasherFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { dishwasherFrequencyUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="dishwasherFrequency"
                            min="1"
                            max="5"
                            step="1"
                            value={formData?.dishwasherFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { dishwasherFrequencyUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Hand Washing Option */}
                <div className="space-y-4">
                  <div
                    onClick={() => updateMethodWithFlag('dishwashingMethod', 'handwash')}
                    className={`p-2 border-2 rounded-lg cursor-pointer
                      ${formData?.dishwashingMethod === 'handwash'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    Hand Washing
                    {isOptionFilled('dishwashingMinutes') && <SmallCheckIcon />}
                  </div>
                  
                  {formData?.dishwashingMethod === 'handwash' && (
                    <div className="ml-4 space-y-4 border-l-2 pl-4 border-blue-200">
                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                          Minutes spent handwashing dishes per day:
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="dishwashingMinutes"
                            min="1"
                            max="60"
                            step="1"
                            value={formData?.dishwashingMinutes || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { dishwashingMinutesUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="dishwashingMinutes"
                            min="1"
                            max="60"
                            value={formData?.dishwashingMinutes || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { dishwashingMinutesUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                         
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Laundry */}
        {renderSection(
          { id: 'laundry', label: '👕 Laundry' },
          <div className="space-y-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                How do you wash your clothes?
              </label>
              <div className="grid grid-cols-1 gap-4 mt-2">
                {/* Washing Machine Option */}
                <div className="space-y-4">
                  <div
                    onClick={() => updateMethodWithFlag('laundryMethod', 'machine')}
                    className={`p-2 border-2 rounded-lg cursor-pointer
                      ${formData?.laundryMethod === 'machine'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    Washing Machine
                  </div>
                  
                  {formData?.laundryMethod === 'machine' && (
                    <div className="ml-4 space-y-4 border-l-2 pl-4 border-blue-200">
                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                          Loads of laundry per week:
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="laundryLoads"
                            min="0"
                            max="10"
                            step="1"
                            value={formData?.laundryLoads || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { laundryLoadsUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="laundryLoads"
                            min="0"
                            max="10"
                            value={formData?.laundryLoads || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { laundryLoadsUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                          Type of washing machine:
                        </label>
                        <div className="grid grid-cols-1 gap-2 mt-2">
                          {[
                            { 
                              id: 'frontLoad', 
                              label: 'Fully Automatic Front Load'
                            },
                            { 
                              id: 'topLoad', 
                              label: 'Fully Automatic Top Load'
                            },
                            { 
                              id: 'semiAuto', 
                              label: 'Semi Automatic'
                            }
                          ].map(type => (
                            <div
                              key={type.id}
                              onClick={() => updateMethodWithFlag('washerType', type.id)}
                              className={`p-2 border-2 rounded-lg cursor-pointer
                                ${formData?.washerType === type.id
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-blue-300'}`}
                            >
                              <span>{type.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                          Do you typically run full loads?
                        </label>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div
                            onClick={() => updateMethodWithFlag('fullLoads', true)}
                            className={`p-2 border-2 rounded-lg cursor-pointer text-center
                              ${formData?.fullLoads === true
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'}`}
                          >
                            Yes
                          </div>
                          <div
                            onClick={() => updateMethodWithFlag('fullLoads', false)}
                            className={`p-2 border-2 rounded-lg cursor-pointer text-center
                              ${formData?.fullLoads === false
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'}`}
                          >
                            No
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Hand Washing Option */}
                <div className="space-y-4">
                  <div
                    onClick={() => updateMethodWithFlag('laundryMethod', 'handwash')}
                    className={`p-2 border-2 rounded-lg cursor-pointer
                      ${formData?.laundryMethod === 'handwash'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    Hand Washing
                  </div>
                  
                  {formData?.laundryMethod === 'handwash' && (
                    <div className="ml-4 space-y-4 border-l-2 pl-4 border-blue-200">
                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                          Number of buckets used per washing session:
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="laundryBuckets"
                            min="1"
                            max="10"
                            step="1"
                            value={formData?.laundryBuckets || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { laundryBucketsUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="laundryBuckets"
                            min="1"
                            max="10"
                            value={formData?.laundryBuckets || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { laundryBucketsUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                        
                        </p>
                      </div>

                      <div>
                        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                          Washing sessions per week:
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            name="handwashingFrequency"
                            min="1"
                            max="7"
                            step="1"
                            value={formData?.handwashingFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { handwashingFrequencyUpdated: true });
                            }}
                            className="w-full"
                          />
                          <input
                            type="number"
                            name="handwashingFrequency"
                            min="1"
                            max="7"
                            value={formData?.handwashingFrequency || 0}
                            onChange={(e) => {
                              handleInputChange(e);
                              updateFormData('indoor', { handwashingFrequencyUpdated: true });
                            }}
                            className="w-20 text-center border rounded p-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IndoorUsage;